/**
 * LoginPage — экран входа в дизайн-системе КОММА.
 *
 *   ┌─────────────────────────────────────────────┐
 *   │  (брендовый градиент + canvas-«созвездие»)   │
 *   │            ┌───────────────────┐             │
 *   │            │ [logo]            │             │
 *   │            │ Кардиограмма клуба│             │
 *   │            │ CLUBFIRST         │             │
 *   │            │ [ Имя пользователя]│            │
 *   │            │ [ Пароль       👁 ]│            │
 *   │            │ [    Войти →     ] │            │
 *   │            └───────────────────┘             │
 *   │       Защищённое соединение · © …            │
 *   └─────────────────────────────────────────────┘
 *
 * Визуальный язык — КОММА (login-bg, Constellation, card). Бренд продукта
 * («Кардиограмма клуба» / ClubFirst) и auth-флоу retention сохранены:
 * username + password, RHF + Zod, login() → redirect /today.
 */

import { useEffect, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useForm, Controller, type SubmitHandler } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Button, Icon, PasswordInput } from '@/shared/ui';
import { Constellation } from '@/components/ui/Constellation';
import { useAuth } from '@/auth/useAuth';
import { useTheme } from '@/shared/theme/ThemeContext';
import { ApiError } from '@/api/client';

const loginSchema = z.object({
  username: z.string().trim().min(2, 'Минимум 2 символа').max(64, 'Максимум 64 символа'),
  password: z.string().min(4, 'Минимум 4 символа').max(128, 'Максимум 128 символов'),
});

type LoginFormValues = z.infer<typeof loginSchema>;

/** Коды из /api/auth/callback (?sso_error=...) — человеческим языком. */
const SSO_ERRORS: Record<string, string> = {
  not_configured: 'Вход через SSO пока не настроен.',
  invalid_roles: 'В КОММА IDM вам не выдана роль для KOMMA Ads. Обратитесь к администратору.',
  missing_claims: 'Keycloak не передал логин пользователя. Обратитесь к администратору.',
  account_conflict: 'Логин уже связан с другой учётной записью Keycloak.',
  invalid_state: 'Сессия входа устарела. Попробуйте ещё раз.',
};

function ssoErrorText(code: string): string {
  return SSO_ERRORS[code] ?? 'Не удалось войти через SSO. Попробуйте ещё раз.';
}

interface AuthConfig {
  sso_enabled: boolean;
  local_login_enabled: boolean;
}

function ErrorBox({ text }: { text: string }) {
  return (
    <div
      className="row center gap8"
      role="alert"
      style={{
        padding: '10px 12px',
        borderRadius: 'var(--r-sm)',
        background: 'var(--danger-bg)',
        color: 'var(--danger-fg)',
        fontSize: 13,
        fontWeight: 700,
      }}
    >
      <Icon name="Alert" style={{ width: 16, height: 16, flex: 'none' }} />
      <span>{text}</span>
    </div>
  );
}

export function LoginPage() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const { theme, toggle } = useTheme();
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [config, setConfig] = useState<AuthConfig>({ sso_enabled: false, local_login_enabled: true });
  // Форма логина/пароля — аварийный путь: прячем её за кнопкой, пока есть SSO.
  // Без SSO (или если конфиг не ответил) показываем сразу — иначе войти нечем.
  const [localRequested, setLocalRequested] = useState(false);
  const showLocal = config.local_login_enabled && (!config.sso_enabled || localRequested);
  const [ssoError] = useState<string | null>(() => new URLSearchParams(window.location.search).get('sso_error'));

  useEffect(() => {
    let cancelled = false;
    void fetch('/api/auth/config')
      .then((r) => (r.ok ? (r.json() as Promise<AuthConfig>) : null))
      .then((c) => {
        if (c && !cancelled) setConfig(c);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, []);

  const {
    register,
    handleSubmit,
    control,
    formState: { errors, isSubmitting },
  } = useForm<LoginFormValues>({
    resolver: zodResolver(loginSchema),
    mode: 'onSubmit',
    defaultValues: { username: '', password: '' },
  });

  const onSubmit: SubmitHandler<LoginFormValues> = async (values) => {
    setSubmitError(null);
    try {
      await login(values.username, values.password);
      await navigate({ to: '/overview' });
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        setSubmitError('Неверный логин или пароль');
      } else if (err instanceof ApiError) {
        setSubmitError(err.detail || 'Не удалось выполнить вход. Попробуйте ещё раз.');
      } else {
        setSubmitError('Сеть недоступна. Проверьте подключение и попробуйте снова.');
      }
    }
  };

  return (
    <div
      className="login-bg row jcenter center"
      style={{ minHeight: '100vh', padding: 20, position: 'relative', overflow: 'hidden' }}
    >
      <Constellation />
      <div
        className="login-orb o1"
        style={{
          top: '-160px',
          right: '8%',
          width: 480,
          height: 480,
          background: 'radial-gradient(circle at 35% 35%, rgba(255,255,255,.20), rgba(255,255,255,0) 65%)',
        }}
      />
      <div
        className="login-orb o2"
        style={{
          bottom: '-180px',
          left: '-60px',
          width: 420,
          height: 420,
          background: 'radial-gradient(circle at 50% 50%, rgba(147,197,253,.18), rgba(147,197,253,0) 68%)',
        }}
      />
      <div
        className="login-orb o3"
        style={{
          top: '30%',
          left: '14%',
          width: 240,
          height: 240,
          background: 'radial-gradient(circle at 50% 50%, rgba(255,255,255,.10), rgba(255,255,255,0) 70%)',
        }}
      />
      <div className="login-grid" />
      <div className="login-vignette" />

      <button
        className="iconbtn"
        onClick={toggle}
        aria-label="Переключить тему"
        style={{
          position: 'absolute',
          top: 20,
          right: 20,
          zIndex: 1,
          color: '#fff',
          background: 'rgba(255,255,255,.14)',
          borderColor: 'transparent',
        }}
      >
        <Icon name={theme === 'dark' ? 'Moon' : 'Sun'} />
      </button>

      <div
        className="card"
        style={{ width: 'min(420px, 100%)', padding: 32, position: 'relative', zIndex: 1 }}
      >
        <div className="col center" style={{ gap: 10, marginBottom: 24 }}>
          <h1 className="sr-only">KOMMA Ads — вход</h1>
          <div
            aria-hidden
            style={{
              width: 64, height: 64, borderRadius: 16, display: 'grid', placeItems: 'center',
              fontWeight: 800, fontSize: 30, color: 'var(--on-primary, #fff)',
              background: 'var(--primary)', boxShadow: 'var(--e2)',
            }}
          >
            K
          </div>
          <div className="col center" style={{ gap: 2 }}>
            <span style={{ fontSize: 22, fontWeight: 800, letterSpacing: '-.02em' }}>
              KOMMA Ads
            </span>
            <span
              className="muted"
              style={{ fontSize: 11.5, fontWeight: 800, letterSpacing: '.14em', textTransform: 'uppercase' }}
            >
              Продвижение Яндекс.Директ
            </span>
          </div>
        </div>

        {ssoError && <ErrorBox text={ssoErrorText(ssoError)} />}

        {config.sso_enabled && (
          <div className="col gap16" style={{ marginBottom: showLocal ? 16 : 0 }}>
            <Button icon="Lock" block onClick={() => { window.location.href = '/api/auth/sso/login'; }}>
              Войти через КОММА SSO
            </Button>
            {config.local_login_enabled && !showLocal && (
              <Button variant="text" size="sm" block onClick={() => setLocalRequested(true)}>
                Войти по паролю
              </Button>
            )}
          </div>
        )}

        {showLocal && (
        <form className="col gap16" onSubmit={handleSubmit(onSubmit)} noValidate>
          <div className="field">
            <label htmlFor="username">Имя пользователя</label>
            <input
              id="username"
              className="input"
              type="text"
              autoComplete="username"
              autoFocus
              disabled={isSubmitting}
              {...register('username')}
            />
            {errors.username && (
              <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--danger-fg)' }}>
                {errors.username.message}
              </span>
            )}
          </div>

          <div className="field">
            <label htmlFor="password">Пароль</label>
            <Controller
              name="password"
              control={control}
              render={({ field }) => (
                <PasswordInput
                  id="password"
                  autoComplete="current-password"
                  value={field.value}
                  onChange={field.onChange}
                  placeholder="••••••••"
                  disabled={isSubmitting}
                />
              )}
            />
            {errors.password && (
              <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--danger-fg)' }}>
                {errors.password.message}
              </span>
            )}
          </div>

          {submitError && <ErrorBox text={submitError} />}

          <Button type="submit" icon="ArrowRight" disabled={isSubmitting} block>
            {isSubmitting ? 'Входим…' : 'Войти в систему'}
          </Button>
        </form>
        )}
      </div>

      <div
        style={{
          position: 'absolute',
          bottom: 18,
          left: 0,
          right: 0,
          zIndex: 1,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          gap: 8,
          color: 'rgba(255,255,255,.72)',
          fontSize: 12.5,
          fontWeight: 600,
          pointerEvents: 'none',
        }}
      >
        <Icon name="Lock" style={{ width: 13, height: 13 }} />
        Защищённое соединение · © 2026 CommunityTech Group
      </div>
    </div>
  );
}
